/**
 * Workspace-type presets - the single source of truth shared by the signup
 * page (the 3 offering cards) and Settings > General (where the same choice can
 * be changed later). Each preset maps to the two soft feature flags:
 *   scheduling -> Calendar / Meetings / Today's calls
 *   students   -> Students / Cohorts / Course finance
 * They are NOT locks: the toggles in Settings can still be flipped, which just
 * lands you on the "Custom" preset.
 *
 * The matching server-side flag map lives in api/src/services/provisioning.js
 * (WORKSPACE_TYPE_FLAGS) - keep the keys in sync.
 */
import { Target, Calendar, GraduationCap, ShoppingBag, Headset } from 'lucide-react';

export const WORKSPACE_PRESETS = [
  {
    key: 'lead_gen',
    label: 'Lead generation',
    tagline: 'Capture & follow up',
    desc: 'Capture leads and follow up on WhatsApp & email. No meetings or courses.',
    icon: Target,
    scheduling: false,
    students: false,
    features: [
      'Lead capture forms & landing pages',
      'Pipeline, stages & lead scoring',
      'Tags & smart segments',
      'WhatsApp + email follow-up',
      'Automations & instant lead alerts',
      'Meta CAPI / GA4 server-side attribution',
    ],
  },
  {
    key: 'appointments',
    label: 'Appointments',
    tagline: 'Leads + meetings',
    desc: 'Everything in Lead Generation, plus a calendar and booked meetings.',
    icon: Calendar,
    scheduling: true,
    students: false,
    recommended: true,
    features: [
      'Everything in Lead Generation',
      'Calendar & booking forms',
      'Meetings & automated reminders',
      'No-show & follow-up flows',
      "Today's calls view",
      'Google Calendar / Meet sync',
    ],
  },
  {
    key: 'coaching',
    label: 'Coaching & courses',
    tagline: 'The full suite',
    desc: 'Everything, plus students, cohorts and finance for course businesses.',
    icon: GraduationCap,
    scheduling: true,
    students: true,
    features: [
      'Everything in Appointments',
      'Students & cohorts',
      'Course enrolment',
      'Payment links via WhatsApp',
      'Payments & invoices',
      'Finance & expense tracking',
    ],
  },
  {
    key: 'store_leads',
    label: 'Store + enquiries',
    tagline: 'Sell online and by phone',
    desc: 'For a store whose sales team also works enquiries - dealers, wholesale, high-value orders. Everything in Online store, plus the lead desk, so a WhatsApp enquiry and the order it becomes are one funnel.',
    icon: Headset,
    scheduling: false,
    students: false,
    ecommerce: true,
    leads: true,
    features: [
      'Everything in Online store',
      'Pipeline, forms, chat & WhatsApp enquiries',
      'Manual and draft orders stitched to the enquiry that caused them',
      'One customer record across enquiry and purchase',
      'Lead source, lead ROAS and store attribution together',
    ],
  },
  {
    key: 'ecommerce',
    label: 'Online store',
    tagline: 'Fix purchase attribution',
    desc: 'For e-commerce. Capture every purchase with its real source, see the match quality, and send enriched conversions to your ad platforms.',
    icon: ShoppingBag,
    scheduling: false,
    students: false,
    ecommerce: true,
    features: [
      'Every purchase captured with its attribution',
      'Match quality (EMQ) per order - not per pixel',
      'Revenue-at-risk from missing click-IDs',
      'Server-side Purchase to Meta / Google / TikTok',
      'Enrich, then fire - dedup against your pixel',
      'Customers, LTV & repeat-purchase workflows',
    ],
  },
];

// The card pre-selected on the signup page: lead-gen WITH meetings.
export const DEFAULT_WORKSPACE_TYPE = 'appointments';

// Which preset (if any) a set of flags corresponds to ('custom' if none).
// `leads` only distinguishes anything once ecommerce is on - a lead-gen
// workspace has the desk regardless - so it defaults false and the pure-store
// preset keeps matching exactly as it did.
export function deriveWorkspaceType(scheduling, students, ecommerce = false, leads = false) {
  const hit = WORKSPACE_PRESETS.find(
    (p) => p.scheduling === scheduling && p.students === students
      && (p.ecommerce || false) === ecommerce && (p.leads || false) === leads
  );
  return hit ? hit.key : 'custom';
}

/**
 * Does this person still owe /welcome its one question?
 *
 * Only a self-serve workspace is marked onboarding.pending (at provisioning),
 * and /welcome's finish writes onboarding.shaped. The app shell asks this on
 * every signed-in load, because the signup and verify screens can no longer
 * guarantee the redirect themselves: mandatory two-step login routes both
 * through the login page, which lands on home. Admins only - the question
 * shapes the whole workspace, and a teammate invited early should not be the
 * one to answer it. Workspaces from before the flag carry neither key.
 */
export function needsWelcome(values, role) {
  if (!values || role !== 'admin') return false;
  return values['onboarding.pending'] === true && values['onboarding.shaped'] !== true;
}
